import type { CollapsedSample } from '../data/collapseCoincident';
import { sampleLattice } from '../data/sampleLattice';
import type { Isoline, Margin, ScalarGrid, Size } from '../types';
import type { ColorScale } from './colorScale';
import { createRampLookup } from './rampLookup';
import { samplePaint, type SurfacePaint } from './surfacePaint';
import { toAxisFractions } from '../data/horizontalAxes';

/** Radius of a sample marker, and the ring marking one the host named as context. */
/* In grid cells, squared-ish: the polygon is in grid coordinates, so a hull thinner than a
   thousandth of one cell of area has nothing to show whatever the resolution. */
const HULL_AREA_EPSILON = 1e-3;

const SAMPLE_RADIUS = 2.5;
const CONTEXT_RING_WIDTH = 1.25;
const CONTEXT_RING_GAP = 2.5;

export type ContourStyle = {
  showFilledBands: boolean;
  showIsolines: boolean;
  /**
   * Mark where components actually sit.
   *
   * The one control on this chart that changes what a viewer can conclude: the field
   * between the marks is interpolated, and only the marks are measured. With them hidden, a
   * confidently coloured region in an empty part of the plane looks exactly like a region
   * backed by fifty components.
   */
  showSamplePositions: boolean;
  opacity: number;
};

export const DEFAULT_CONTOUR_STYLE: ContourStyle = {
  showFilledBands: true,
  showIsolines: true,
  showSamplePositions: true,
  opacity: 1,
};

export type ContourRenderArgs = {
  context: CanvasRenderingContext2D;
  grid: ScalarGrid;
  isolines: Isoline[];
  /** One entry per distinct position, so a stack of components is one dot rather than forty. */
  samples: CollapsedSample[];
  viewport: Size;
  margin: Margin;
  colorScale: ColorScale;
  /** Colors the bands in place of the ramp; null colors them by height. */
  paint: SurfacePaint | null;
  style: ContourStyle;
  inkColor: string;
  surfaceColor: string;
};

type PlotArea = { left: number; top: number; width: number; height: number };

const toPlotArea = (viewport: Size, margin: Margin): PlotArea => ({
  left: margin.left,
  top: margin.top,
  width: Math.max(0, viewport.width - margin.left - margin.right),
  height: Math.max(0, viewport.height - margin.top - margin.bottom),
});

/**
 * Paints the field a pixel at a time through an ImageData buffer.
 *
 * A rect per grid cell would be far fewer draw calls but shows the lattice as visible
 * blocks, and the lattice is an artefact of the gridding rather than anything in the data.
 * Writing pixels keeps the banding - which is meaningful, since a band edge is an isoline -
 * and drops the blocking, which is not.
 */
/**
 * The x-range the hull covers at one grid row, or null where the row misses it entirely.
 *
 * A span per row rather than a containment test per pixel: the hull is convex, so one horizontal
 * line crosses it at most twice, and the whole row is then a comparison against two numbers. This
 * runs inside the band loop, which the ramp lookup above was already measured at 587 ms without.
 */
const hullSpanAt = (
  polygon: [number, number][],
  y: number
): [number, number] | null => {
  let min = Infinity;
  let max = -Infinity;

  for (let index = 0; index < polygon.length; index++) {
    const [ax, ay] = polygon[index];
    const [bx, by] = polygon[(index + 1) % polygon.length];
    if ((ay <= y && by >= y) || (by <= y && ay >= y)) {
      const crossing = ay === by ? ax : ax + ((y - ay) / (by - ay)) * (bx - ax);
      if (crossing < min) min = crossing;
      if (crossing > max) max = crossing;
    }
  }

  return min <= max ? [min, max] : null;
};

/** The hull in lattice coordinates, which is the space the band loop already works in. */
/**
 * Whether the hull encloses anything, which is not the same question as how many vertices it has.
 *
 * `toHull` reads `delaunay.hull` back through the *original* sample coordinates, and the
 * triangulation jitters its own copy to survive degenerate input - so an exactly collinear estate
 * comes back as three or more collinear vertices rather than as fewer than three. Counting them
 * says the hull is fine; its area says it is a line. Clipping to a line erases the chart, and
 * masking to one leaves every span zero-width, so both callers ask this instead.
 *
 * Unreachable under `linear`, where such a grid is empty before it gets here, and reachable under
 * `smooth` and `binned`, which fill without needing an area to interpolate over.
 */
const enclosesArea = (polygon: [number, number][]): boolean => {
  if (polygon.length < 3) return false;

  const twiceArea = polygon.reduce((total, [x, y], index) => {
    const [nextX, nextY] = polygon[(index + 1) % polygon.length];
    return total + (x * nextY - nextX * y);
  }, 0);

  return Math.abs(twiceArea) > HULL_AREA_EPSILON;
};

const toGridPolygon = (grid: ScalarGrid): [number, number][] =>
  grid.hull.map(([x, y]) => [
    toGridCoordinate(x, grid.dataXRange, grid.width),
    toGridCoordinate(y, grid.dataYRange, grid.height),
  ]);

const paintBands = (
  context: CanvasRenderingContext2D,
  grid: ScalarGrid,
  area: PlotArea,
  colorScale: ColorScale,
  paint: SurfacePaint | null,
  opacity: number
): void => {
  /* `putImageData` writes raw device pixels and ignores the context transform, unlike every
     other drawing call here. The canvas is scaled by the device pixel ratio so the chart is
     sharp on a retina display, so the buffer has to be built and placed in device pixels or
     it lands at a fraction of its intended size in the corner of the plot. Read off the
     transform rather than passed in, so the renderer cannot disagree with its own canvas. */
  const { a: scaleX, d: scaleY } = context.getTransform();
  const width = Math.floor(area.width * scaleX);
  const height = Math.floor(area.height * scaleY);
  /* One device pixel is as fatal as none: the loops below map a pixel onto the grid with
     `pixelX / (width - 1)`, which at a width of 1 is 0/0. The NaN then survives every check on the
     way down - `sampleLattice` indexes `grid.values[NaN]`, gets `undefined`, and passes a null test
     written as `=== null` - until `colorScale.at` indexes its ramp with NaN and dereferences
     `undefined`. Inside a `requestAnimationFrame` callback that escapes as an uncaught error. */
  if (width <= 1 || height <= 1) return;

  const image = context.createImageData(width, height);
  const alpha = Math.round(255 * opacity);

  /* The ramp baked once rather than evaluated per pixel. `forValue` runs nine `Math.pow` calls and
     allocates an array, and at the default `bands: 9` the whole image holds nine distinct colours -
     so almost all of it was recomputing the same triples. Measured at 587 ms for a 1200x900 pane at
     device pixel ratio 2, on the one chart with no GPU path and no `rasterScale` to fall back to.

     Sized to the scale's own quantisation rather than to a fixed texel count. A 256-entry table
     does not divide by nine, so a band boundary lands inside a texel and gets displaced by up to
     half of one - and on this chart the band edge *is* the isoline, so a displaced boundary is
     exactly the defect the isoline count was fixed for. One entry per band is exact; a continuous
     ramp has no boundary to move and takes the fixed table. */
  const ramp = createRampLookup(colorScale);
  const painted: [number, number, number] = [0, 0, 0];

  /* Masked here rather than left to `clipToHull`, which cannot reach these pixels: the spec exempts
     `putImageData` from the clipping region as well as from the transform, and the comment at the
     top of this function knew only half of that. Without this the dilation that lets the fill reach
     the hull is never trimmed back, so the bands run four cells past the data while the isolines -
     stroked paths, and so genuinely clipped - stop at it. */
  const polygon = toGridPolygon(grid);
  const masked = enclosesArea(polygon);

  /* The inverse of `toScreen`, which is where this arithmetic would otherwise quietly stop
     agreeing with the paths drawn over these bands. A widened axis leaves pixels either side of
     the lattice, and those are outside the field rather than at the edge of it: read without the
     bounds check they clamp, and the boundary row smears across the padding as solid colour. */
  const [xLow, xHigh] = toAxisFractions(grid.dataXRange, grid.xRange);
  const [yLow, yHigh] = toAxisFractions(grid.dataYRange, grid.yRange);
  const xFractionSpan = xHigh - xLow || 1;
  const yFractionSpan = yHigh - yLow || 1;

  for (let pixelY = 0; pixelY < height; pixelY++) {
    /* Screen y grows downward and the grid's rows grow upward, so the row is flipped here
       rather than in the sampler. */
    const gridY =
      (((height - 1 - pixelY) / (height - 1) - yLow) / yFractionSpan) *
      (grid.height - 1);
    const span = masked ? hullSpanAt(polygon, gridY) : null;
    const outsideRow = gridY < 0 || gridY > grid.height - 1;
    for (let pixelX = 0; pixelX < width; pixelX++) {
      const gridX =
        ((pixelX / (width - 1) - xLow) / xFractionSpan) * (grid.width - 1);
      const offset = (pixelY * width + pixelX) * 4;
      if (outsideRow || gridX < 0 || gridX > grid.width - 1) {
        image.data[offset + 3] = 0;
        continue;
      }
      if (masked && (!span || gridX < span[0] || gridX > span[1])) {
        image.data[offset + 3] = 0;
        continue;
      }
      const value = sampleLattice(grid, gridX, gridY, 'propagate');
      if (value === null) {
        image.data[offset + 3] = 0;
        continue;
      }
      if (paint) {
        samplePaint(
          paint,
          (pixelX / (width - 1)) * 2 - 1,
          ((height - 1 - pixelY) / (height - 1)) * 2 - 1,
          painted
        );
      }
      const [r, g, b] = paint ? painted : ramp.nearest(value);
      image.data[offset] = r;
      image.data[offset + 1] = g;
      image.data[offset + 2] = b;
      image.data[offset + 3] = alpha;
    }
  }

  context.putImageData(image, area.left * scaleX, area.top * scaleY);
};

export type ProjectedSample = {
  sample: CollapsedSample;
  screenX: number;
  screenY: number;
};

/**
 * Where each sample's marker lands.
 *
 * Exported so a hover can hit-test against the same numbers the markers were drawn from. Computing
 * them a second time in the component would be a second chance to disagree about where a point is,
 * and the symptom would be a tooltip naming the mark next to the one under the cursor.
 */
export const projectSamples = (
  grid: ScalarGrid,
  samples: CollapsedSample[],
  viewport: Size,
  margin: Margin
): ProjectedSample[] => {
  const area = toPlotArea(viewport, margin);
  if (area.width <= 1 || area.height <= 1) return [];

  return samples.map(sample => {
    const [screenX, screenY] = toScreen(
      toGridCoordinate(sample.x, grid.dataXRange, grid.width),
      toGridCoordinate(sample.y, grid.dataYRange, grid.height),
      grid,
      area
    );
    return { sample, screenX, screenY };
  });
};

/**
 * Confines what follows to the samples' own outline.
 *
 * A no-op when the hull has no area - fewer than three points, or all of them collinear - because
 * clipping to a degenerate path would erase the chart rather than bound it, and that case already
 * has its own empty state.
 */
const clipToHull = (
  context: CanvasRenderingContext2D,
  grid: ScalarGrid,
  area: PlotArea
): void => {
  if (!enclosesArea(toGridPolygon(grid))) return;

  context.beginPath();
  grid.hull.forEach(([x, y], index) => {
    const gridX = toGridCoordinate(x, grid.dataXRange, grid.width);
    const gridY = toGridCoordinate(y, grid.dataYRange, grid.height);
    const [screenX, screenY] = toScreen(gridX, gridY, grid, area);
    if (index === 0) {
      context.moveTo(screenX, screenY);
      return;
    }
    context.lineTo(screenX, screenY);
  });
  context.closePath();
  context.clip();
};

/** Field units to lattice coordinates, the inverse of what `toScalarGrid` rasterised with. */
const toGridCoordinate = (
  value: number,
  [min, max]: [number, number],
  samples: number
): number => ((value - min) / (max - min || 1)) * (samples - 1);

/**
 * A lattice coordinate to a pixel in the plot area.
 *
 * The lattice fills the frame only when the axes are its own extent. Widened, it occupies the
 * sub-rectangle {@link toAxisFractions} names.
 */
const toScreen = (
  gridX: number,
  gridY: number,
  grid: ScalarGrid,
  area: PlotArea
): [number, number] => {
  const [xLow, xHigh] = toAxisFractions(grid.dataXRange, grid.xRange);
  const [yLow, yHigh] = toAxisFractions(grid.dataYRange, grid.yRange);

  return [
    area.left +
      (xLow + (gridX / (grid.width - 1)) * (xHigh - xLow)) * area.width,
    area.top +
      area.height -
      (yLow + (gridY / (grid.height - 1)) * (yHigh - yLow)) * area.height,
  ];
};

export const renderContour = ({
  context,
  grid,
  isolines,
  samples,
  viewport,
  margin,
  colorScale,
  paint,
  style,
  inkColor,
  surfaceColor,
}: ContourRenderArgs): void => {
  if (grid.width < 2 || grid.height < 2) return;

  const area = toPlotArea(viewport, margin);
  /* Nothing at all when the pane is narrower than its own gutters. `toPlotArea` floors both
     dimensions at zero, and only the band fill noticed - so `toScreen` mapped every grid
     coordinate onto `margin.left` and the isolines and markers were drawn as a stripe of ink in
     the gutter, with no frame beside them because `renderAxes2d` bails on the same condition. A
     chart out of room should look like one, not like a rendering fault.

     Round 22's measured gutters moved the trigger from about 72px to about 185px for a
     currency-formatted field, which is inside what a divider drag reaches. */
  if (area.width <= 1 || area.height <= 1) return;

  context.save();

  /* Its own save/restore, closed before the markers below. A clip set here and left open applies to
     everything drawn afterwards, which cropped every marker sitting on the boundary into a
     half-circle - the markers are the data, and the one thing that must not be trimmed to fit a
     rendering of it.

     Clips the isolines only. The bands are written with `putImageData`, which the spec exempts from
     the clipping region, so they mask themselves against the same hull inside `paintBands`.

     Clipped to the data's own boundary, not to the cells that happened to fill.
     `toScalarGrid` fills a cell when a triangle claims its *centre*, so the edge of the filled
     region is quantised to the lattice - a stair-stepped border, and a sample near the edge left
     outside it because its cell's centre fell short even though the sample did not. Both are the
     same artefact, and clipping to the hull removes both: the border becomes the straight lines
     the triangulation actually has, and nothing can be drawn outside a region the samples define.
     Applied to the bands and the isolines; the markers sit outside the clip because they are the
     data rather than a rendering of it, and by construction they are inside the hull anyway. */
  context.save();
  clipToHull(context, grid, area);

  if (style.showFilledBands) {
    paintBands(context, grid, area, colorScale, paint, style.opacity);
  }

  if (style.showIsolines) {
    context.strokeStyle = inkColor;
    context.globalAlpha = 0.45 * style.opacity;
    context.lineWidth = 1;
    context.lineJoin = 'round';
    for (const { points: isolinePoints } of isolines) {
      context.beginPath();
      isolinePoints.forEach(([gridX, gridY], index) => {
        const [screenX, screenY] = toScreen(gridX, gridY, grid, area);
        if (index === 0) {
          context.moveTo(screenX, screenY);
          return;
        }
        context.lineTo(screenX, screenY);
      });
      context.stroke();
    }
  }

  /* End of the clipped field. */
  context.restore();

  if (style.showSamplePositions) {
    const [xMin, xMax] = grid.dataXRange;
    const [yMin, yMax] = grid.dataYRange;
    context.globalAlpha = 0.8 * style.opacity;
    context.strokeStyle = surfaceColor;
    context.lineWidth = 2;
    context.fillStyle = inkColor;

    /* Small, ringed in the surface colour, and drawn last: they have to survive on top of
       any band colour without being mistaken for part of the field. */
    for (const point of samples) {
      const [screenX, screenY] = toScreen(
        ((point.x - xMin) / (xMax - xMin || 1)) * (grid.width - 1),
        ((point.y - yMin) / (yMax - yMin || 1)) * (grid.height - 1),
        grid,
        area
      );
      context.beginPath();
      context.arc(screenX, screenY, SAMPLE_RADIUS, 0, Math.PI * 2);
      context.stroke();
      context.fill();

      /* A component the host named, marked out the same way the point charts and the surface do -
         so one `highlightedComponentId` reaches all four charts rather than two. Emphasis rather
         than dimming: context lasts as long as the view is open. */
      if (point.points.some(({ isContext }) => isContext)) {
        context.strokeStyle = inkColor;
        context.lineWidth = CONTEXT_RING_WIDTH;
        context.beginPath();
        context.arc(
          screenX,
          screenY,
          SAMPLE_RADIUS + CONTEXT_RING_GAP,
          0,
          Math.PI * 2
        );
        context.stroke();
        context.strokeStyle = surfaceColor;
        context.lineWidth = 2;
      }
    }
  }

  context.restore();
};
